import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, FileText, FlaskConical, Loader2, Sparkles, Table2, Users } from '@digital-twins/design-system/icons'
import type { LibraryItemRef } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import {
  COLLECTION_SELECTION_MAX,
  collectionItemKey,
  dedupeCollectionSelection,
  toggleCollectionSelection,
} from './collectionSelection'
import { useCollectionCandidates } from './useCollectionCandidates'

export interface CollectionContentsPickerProps {
  selected: Array<LibraryItemRef>
  onChange: (refs: Array<LibraryItemRef>) => void
  socialEnabled: boolean
  disabled?: boolean
}

const KIND_ICONS = { study: FlaskConical, audience: Users, dataset: Table2, document: FileText, topic: Sparkles } as const

/** Suggestions attach only opaque refs. Seeded items need no title lookup. */
export function CollectionContentsPicker({ selected, onChange, socialEnabled, disabled = false }: CollectionContentsPickerProps) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')
  const candidates = useCollectionCandidates({ query, socialEnabled, enabled: !disabled })
  const refs = dedupeCollectionSelection(selected)
  const selectedKeys = new Set(refs.map(collectionItemKey))
  const atLimit = refs.length >= COLLECTION_SELECTION_MAX

  return (
    <section className="flex min-w-0 flex-col gap-3" data-testid="collection-contents-picker">
      <Input
        value={query}
        maxLength={200}
        disabled={disabled}
        aria-label={t('library.collections.dialog.searchContents')}
        placeholder={t('library.collections.dialog.searchContents')}
        data-testid="collection-contents-search"
        onChange={event => setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter') event.preventDefault() }}
      />
      <div className="flex items-center justify-between gap-3">
        <Text size="sm" color="muted" data-testid="collection-selection-count">
          {t('library.collections.dialog.selectionCount', { count: refs.length })}
        </Text>
        {refs.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange([])} data-testid="collection-selection-clear">
            {t('library.collections.dialog.clearSelection')}
          </Button>
        ) : null}
      </div>
      {atLimit ? <Text size="xs" color="muted" role="status">{t('library.collections.dialog.selectionLimit', { count: COLLECTION_SELECTION_MAX })}</Text> : null}
      <Text size="xs" color="muted">{t('library.collections.dialog.suggestionsHint')}</Text>
      {candidates.isLoading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin text-muted-foreground" data-testid="collection-candidates-loading" /> : null}
      {candidates.hasError ? (
        <div className="flex items-center gap-2" role="alert">
          <Text size="xs" color="muted">{t('library.collections.dialog.candidatesFailed')}</Text>
          <Button type="button" variant="ghost" size="sm" disabled={disabled || candidates.isLoading} onClick={() => { void candidates.retry() }} data-testid="collection-candidates-retry">
            {t('library.collections.dialog.retry')}
          </Button>
        </div>
      ) : null}
      {candidates.hasQuery && !candidates.isLoading && !candidates.hasError && candidates.items.length === 0 ? (
        <Text size="sm" color="muted" data-testid="collection-candidates-empty">{t('library.collections.dialog.noCandidates')}</Text>
      ) : null}
      {candidates.items.length > 0 ? (
        <ul className="flex flex-col gap-1" data-testid="collection-candidates-results">
          {candidates.items.map((item, index) => {
            const picked = selectedKeys.has(collectionItemKey(item))
            const Icon = KIND_ICONS[item.kind]
            return (
              <li key={collectionItemKey(item)}>
                <button
                  type="button"
                  disabled={disabled || (atLimit && !picked)}
                  aria-pressed={picked}
                  data-testid={`collection-candidate-${index}`}
                  className={cn('flex w-full min-w-0 items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50', picked && 'bg-accent')}
                  onClick={() => onChange(toggleCollectionSelection(refs, item))}
                >
                  <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <Text as="span" size="sm" className="block truncate" data-testid={`collection-candidate-${index}-title`}>{item.title || t(item.kind === 'topic' ? 'library.collections.picker.tabs.topics' : `library.collections.resources.kinds.${item.kind}`)}</Text>
                    <Text as="span" size="xs" color="muted" className="block">{t(item.kind === 'topic' ? 'library.collections.picker.tabs.topics' : `library.collections.resources.kinds.${item.kind}`)}</Text>
                  </span>
                  {picked ? <Check aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </section>
  )
}
